import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { db } from "../firebase";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { toast } from "react-toastify";
import { logService } from "../utils/logService";

const AgentList = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const {
    user,
    isAdmin,
    isModerator,
    userRole,
    loading: authLoading,
  } = useAuth();

  useEffect(() => {
    const fetchAgents = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      try {
        if (!user) {
          setAgents([]);
          setLoading(false);
          return;
        }

        const querySnapshot = await getDocs(collection(db, "agents"));
        let agentsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter agents based on user role
        if (userRole === "User") {
          // Regular users can only see their own agents
          agentsList = agentsList.filter((agent) => agent.userId === user.uid);
        }
        // Admins and Moderators can see all agents (no filtering)
        setAgents(agentsList);
      } catch (e) {
        console.error("Error fetching agents from Firebase:", e);
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, [user, userRole, authLoading]);

  const downloadJSON = (agent) => {
    const blob = new Blob([JSON.stringify(agent, null, 2)], {
      type: "application/json",
    });
    saveAs(blob, `agent-${agent["Agent Name"] || "data"}.json`);
  };

  const downloadCSV = (agent) => {
    const keys = Object.keys(agent);
    const values = Object.values(agent);
    const csv = keys.join(",") + "\n" + values.join(",");
    const blob = new Blob([csv], { type: "text/csv" });
    saveAs(blob, `agent-${agent["Agent Name"] || "data"}.csv`);
  };

  const renderStatusBadge = (status) => {
    const statusConfig = {
      Verified: { color: "bg-green-600" },
      "Under Review": { color: "bg-blue-600" },
      Pending: { color: "bg-yellow-600" },
      Unverified: { color: "bg-red-600" },
    };

    const config = statusConfig[status] || { color: "bg-gray-600" };

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold text-white ${config.color}`}
      >
        {status || "Unknown"}
      </span>
    );
  };

  const updateVerificationStatus = async (agentId, newStatus) => {
    try {
      const agentRef = doc(db, "agents", agentId);
      const agent = agents.find((a) => a.id === agentId);
      const oldStatus = agent?.["Verification Status"] || "Unknown";

      await updateDoc(agentRef, {
        "Verification Status": newStatus,
      });

      // Log the status change
      await logService.createLog(
        logService.actions.AGENT_STATUS_CHANGED,
        {
          agentId: agentId,
          agentName: agent?.["Agent Name"] || "Unknown Agent",
          oldStatus: oldStatus,
          newStatus: newStatus,
        },
        {
          uid: user?.uid,
          email: user?.email,
          displayName: user?.displayName,
        }
      );

      // Update local state
      setAgents(
        agents.map((agent) =>
          agent.id === agentId
            ? { ...agent, "Verification Status": newStatus }
            : agent
        )
      );

      toast.success(`Verification status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating verification status:", error);
      toast.error("Failed to update verification status");
    }
  };

  const downloadPDF = (agent) => {
    const doc = new jsPDF();

    // App Title
    doc.setFontSize(20);
    doc.text("Valia - Agent Passport", 10, 18);
    doc.setFontSize(12);

    // Horizontal line
    doc.setDrawColor(200, 200, 200);
    doc.line(10, 22, 200, 22);

    // Define field sections matching AgentDetail page
    const registerFields = [
      "Agent ID",
      "Agent Name",
      "Agent Type",
      "Version",
      "Organization",
      "Agent Description",
      "Intent Scope",
      "Language Models",
      "Guardrails",
      "Creation Date",
      "created_by_name",
    ];

    const validationFields = [
      "Reputation Score",
      "Failure Rate",
      "Violations",
      "Provenance",
      "Comments",
      "Verification Status",
    ];

    let y = 32;

    // Agent Registration Information Section
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("Agent Registration Information", 10, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");

    registerFields.forEach((field) => {
      const value = agent[field] || "—";
      doc.text(`${field}:`, 12, y);
      doc.text(String(value), 60, y);
      y += 6;

      if (y > 280) {
        doc.addPage();
        y = 20;
      }
    });

    // Add space between sections
    y += 10;
    if (y > 280) {
      doc.addPage();
      y = 20;
    }

    // Agent Validation & Performance Section
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("Agent Validation & Performance", 10, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");

    validationFields.forEach((field) => {
      const value = agent[field] || "—";
      doc.text(`${field}:`, 12, y);
      doc.text(String(value), 60, y);
      y += 6;

      if (y > 280) {
        doc.addPage();
        y = 20;
      }
    });

    doc.save(`agent-${agent["Agent Name"] || "data"}.pdf`);
  };

  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="valia-spinner h-12 w-12 mx-auto mb-4"></div>
          <span className="text-valia-gray-600 text-lg">Loading agents...</span>
        </div>
      </div>
    );
  }

  if (!agents || agents.length === 0) {
    return (
      <div className="text-center text-valia-gray-600 py-8 sm:py-12 px-4">
        <svg
          className="w-12 h-12 sm:w-16 sm:h-16 text-valia-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        <p className="text-base sm:text-lg mb-4">No agents found.</p>
        {userRole !== "Moderator" && (
          <button
            onClick={() => navigate("/register-agent")}
            className="valia-btn-primary valia-btn-sm"
          >
            Register Your First Agent
          </button>
        )}
      </div>
    );
  }

  // Define the fields to display in the specified order
  const displayFields = [
    "Agent Name",
    "Reputation Score",
    "Verification Status",
    "Creation Date",
    "created_by_name",
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-valia-primary mb-2">
              Agent List
            </h1>
            <p className="text-valia-gray-600 text-base">
              Manage and view all your registered agents
            </p>
          </div>
          {userRole !== "Moderator" && (
            <button
              onClick={() => navigate("/register-agent")}
              className="valia-btn-primary valia-btn-sm flex items-center gap-2 self-start sm:self-auto"
            >
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Register Agent
            </button>
          )}
        </div>
      </div>

      <div>
        {/* Mobile Card Layout */}
        <div className="block lg:hidden space-y-4">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="valia-card p-4 sm:p-6 cursor-pointer"
              onClick={() => navigate(`/agents/${agent.id}`)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-valia-gray-900 truncate">
                    {agent["Agent Name"] || "Unnamed Agent"}
                  </h3>
                  <p className="text-valia-gray-600 text-sm truncate">
                    {agent["Agent Type"] || "Unknown Type"}
                  </p>
                </div>
                {isAdmin ? (
                  <select
                    value={agent["Verification Status"] || ""}
                    onChange={(e) =>
                      updateVerificationStatus(agent.id, e.target.value)
                    }
                    className="bg-white text-valia-gray-900 border border-valia-gray-300 rounded-valia px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-valia-primary focus:border-valia-primary flex-shrink-0 ml-3"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Verified">Verified</option>
                    <option value="Unverified">Unverified</option>
                  </select>
                ) : (
                  <div
                    className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-3 ${
                      agent["Verification Status"] &&
                      agent["Verification Status"]
                        .toLowerCase()
                        .includes("verified")
                        ? "bg-green-600 text-white"
                        : agent["Verification Status"] &&
                          agent["Verification Status"]
                            .toLowerCase()
                            .includes("pending")
                        ? "bg-yellow-600 text-white"
                        : "bg-gray-600 text-white"
                    }`}
                  >
                    {agent["Verification Status"] || "Unknown"}
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-valia-gray-500 text-xs">Reputation</p>
                    <p className="text-valia-gray-900 text-sm truncate">
                      {agent["Reputation Score"] || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-valia-gray-500 text-xs">Agent ID</p>
                    <p className="text-valia-gray-900 text-sm truncate">
                      {agent["Agent ID"] || "—"}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-valia-gray-500 text-xs">Creation Date</p>
                    <p className="text-valia-gray-900 text-sm truncate">
                      {agent["Creation Date"] || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-valia-gray-500 text-xs">Created By</p>
                    <p className="text-valia-gray-900 text-sm truncate">
                      {agent["created_by_name"] || "—"}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-valia-gray-500 text-xs mb-2">Status</p>
                  {renderStatusBadge(agent["Verification Status"])}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/agents/${agent.id}`);
                  }}
                  className="flex-1 valia-btn-primary valia-btn-sm"
                >
                  View Details
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/agents/${agent.id}/history`);
                  }}
                  className="valia-btn-success valia-btn-sm"
                  title="View History"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                {(isModerator || isAdmin) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/agents/${agent.id}/validate`);
                    }}
                    className="valia-btn-warning valia-btn-sm"
                    title="Validate Agent"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadPDF(agent);
                  }}
                  className="valia-btn-secondary valia-btn-sm"
                  title="Download PDF"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table Layout */}
        <div className="hidden lg:block">
          <div className="overflow-x-auto bg-white rounded-valia-lg shadow-valia border border-valia-gray-200">
            <table className="w-full">
              <thead>
                <tr className="bg-valia-gray-50 border-b border-valia-gray-200">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/4">
                    Agent
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/6">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/6">
                    Reputation
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/6">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/5">
                    Created Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-valia-gray-700 uppercase tracking-wide w-1/6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-valia-gray-200">
                {agents.map((agent, idx) => (
                  <tr
                    key={agent.id}
                    className="hover:bg-valia-gray-50 transition-colors duration-200 cursor-pointer"
                    onClick={() => navigate(`/agents/${agent.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-valia-gray-900">
                          {agent["Agent Name"] || "Unnamed Agent"}
                        </div>
                        <div className="text-valia-gray-600 text-xs">
                          {agent["Agent ID"] || "No ID"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-valia-gray-900">
                      {agent["Agent Type"] || "Unknown"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-valia-gray-900">
                      {agent["Reputation Score"] || "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {agent["Verification Status"] ? (
                        isAdmin ? (
                          <select
                            value={agent["Verification Status"] || ""}
                            onChange={(e) =>
                              updateVerificationStatus(agent.id, e.target.value)
                            }
                            className="bg-white text-valia-gray-900 border border-valia-gray-300 rounded-valia px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-valia-primary focus:border-valia-primary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Under Review">Under Review</option>
                            <option value="Verified">Verified</option>
                            <option value="Unverified">Unverified</option>
                          </select>
                        ) : (
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              agent["Verification Status"]
                                .toLowerCase()
                                .includes("verified")
                                ? "bg-green-100 text-green-800"
                                : agent["Verification Status"]
                                    .toLowerCase()
                                    .includes("pending")
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {agent["Verification Status"]}
                          </span>
                        )
                      ) : (
                        <span className="text-valia-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-valia-gray-900">
                      {agent["Creation Date"] || "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/agents/${agent.id}`);
                          }}
                          className="px-2 py-1 text-xs font-medium bg-valia-primary hover:bg-valia-primary-light text-white rounded-valia transition-colors"
                        >
                          View
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/agents/${agent.id}/history`);
                          }}
                          className="px-2 py-1 text-xs font-medium bg-valia-accent hover:bg-valia-accent-light text-white rounded-valia transition-colors"
                        >
                          History
                        </button>
                        {(isModerator || isAdmin) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/agents/${agent.id}/validate`);
                            }}
                            className="px-2 py-1 text-xs font-medium bg-valia-warning hover:bg-orange-600 text-white rounded-valia transition-colors"
                          >
                            Validate
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPDF(agent);
                          }}
                          className="px-2 py-1 text-xs font-medium bg-valia-gray-600 hover:bg-valia-gray-700 text-white rounded-valia transition-colors"
                        >
                          PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentList;
